import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import bilditLogo from '../assets/images/BILDIT_logo.svg';
import bannerReorderDemo from '../assets/images/CMA-ChangeBanner_Reorder.mp4';
import { functions } from '../components/AppContext';
import { INTERNAL_NOTIFY_TO } from '../constants/bilditContact.js';
import { getCallableFriendlyErrorMessage } from '../utils/callableErrors';
import { trackCustomerIoAccountRecovery } from '../utils/customerio';
import '../assets/css/SignupSplit.css';

const PRIVACY_URL = 'https://bildit.co/privacy-policy';
const TERMS_URL = 'https://bildit.co/terms-conditions';

function isEmailQuery(q) {
  const t = q.trim();
  return !(!t.includes('@'));
}

/**
 * Free-trial account lookup: site name shows engine URL; work email resends welcome email.
 */
function AccountRecovery() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteResult, setSiteResult] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    document.title = 'Find your account | BILDIT';
  }, []);

  useEffect(() => {
    const previous = document.body.style.cssText;
    document.body.style.cssText =
      'margin:0;padding:0;min-height:100vh;display:block;overflow-x:hidden;background:#fafafa;background-image:none;';
    return () => {
      document.body.style.cssText = previous;
    };
  }, []);

  const lookupSite = httpsCallable(functions, 'lookupBilditSignupSite');
  const resendEmail = httpsCallable(functions, 'resendFreeTrialWelcomeEmail');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSiteResult(null);
    setInfoMessage('');
    const q = query.trim();
    if (!q) {
      setError('Enter your site name or email.');
      return;
    }

    setLoading(true);
    try {
      if (isEmailQuery(q)) {
        const res = await resendEmail({ email: q });
        const data = res.data;
        if (!data) {
          setError('Something went wrong. Try again.');
          return;
        }
        if (data.code === 400) {
          setError(data.message || 'Check your email address.');
          trackCustomerIoAccountRecovery('welcome_email_resend_failed', {
            lookup_type: 'email',
            error_reason: data.message || 'invalid_email',
          });
          return;
        }
        if (data.code !== 200) {
          setError(data.message || 'Something went wrong.');
          trackCustomerIoAccountRecovery('welcome_email_resend_failed', {
            lookup_type: 'email',
            error_reason: data.message || 'request_failed',
          });
          return;
        }
        setInfoMessage(data.message || '');
        if (data.sent) {
          trackCustomerIoAccountRecovery(
            'welcome_email_resent',
            { lookup_type: 'email' },
            q
          );
        } else {
          trackCustomerIoAccountRecovery(
            'welcome_email_resend_not_found',
            { lookup_type: 'email' },
            q.includes('@') ? q : null
          );
        }
      } else {
        const res = await lookupSite({ siteNameOrDisplayName: q });
        const data = res.data;
        if (!data) {
          setError('Something went wrong. Try again.');
          trackCustomerIoAccountRecovery('site_lookup_failed', {
            lookup_type: 'site_name',
            error_reason: 'missing_response',
          });
          return;
        }
        if (data.code === 404) {
          setError(data.message || 'No site found.');
          trackCustomerIoAccountRecovery('site_lookup_not_found', {
            lookup_type: 'site_name',
          });
          return;
        }
        if (data.code !== 200) {
          setError(data.message || 'Something went wrong.');
          trackCustomerIoAccountRecovery('site_lookup_failed', {
            lookup_type: 'site_name',
            error_reason: data.message || 'request_failed',
          });
          return;
        }
        trackCustomerIoAccountRecovery('site_lookup_success', {
          lookup_type: 'site_name',
          deploy_pending: Boolean(data.deployPending),
        });
        setSiteResult({
          siteName: data.siteName,
          engineUrl: data.engineUrl,
          deployPending: data.deployPending,
        });
      }
    } catch (err) {
      console.error('Account recovery error:', err);
      setError(getCallableFriendlyErrorMessage(err));
      trackCustomerIoAccountRecovery('request_failed', {
        lookup_type: isEmailQuery(q) ? 'email' : 'site_name',
        error_reason: err.message || 'unexpected_error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-split-page">
      <div className="signup-split-left">
        <header>
          <Link to="/signup" className="signup-split-brand-link">
            <img src={bilditLogo} alt="BILDIT" className="signup-split-logo" />
          </Link>
        </header>

        <div className="signup-split-main">
          <h1 className="signup-split-title">Already have an account?</h1>
          <p className="signup-split-subtitle">
            Find your Visual Experience Engine
          </p>
          <p className="signup-split-hint">
            Search by the site name you used at signup to see your engine URL.
            Or enter your email and we&apos;ll resend your free-trial login
            email from {INTERNAL_NOTIFY_TO}.
          </p>

          <form className="signup-split-form" onSubmit={handleSubmit}>
            <div>
              <label
                className="signup-split-label"
                htmlFor="account-recovery-query"
              >
                Site name or email
              </label>
              <input
                id="account-recovery-query"
                name="recovery-query"
                type="text"
                autoComplete="username"
                className="signup-split-input"
                placeholder="Acme Store or you@company.com"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={loading}
              />
            </div>
            {error ? (
              <p className="signup-split-inline-error" role="alert">
                {error}
              </p>
            ) : null}
            {infoMessage ? (
              <output htmlFor="account-recovery-query" className="signup-split-recovery-success">
                {infoMessage}
              </output>
            ) : null}
            {siteResult ? (
              <section
                className="signup-split-recovery-result"
                aria-label="Site lookup result"
              >
                <p className="signup-split-recovery-site-key">
                  Site key: <strong>{siteResult.siteName}</strong>
                </p>
                {siteResult.engineUrl ? (
                  <div className="signup-split-recovery-engine-block">
                    <p className="signup-split-recovery-link-line">
                      Your engine:
                    </p>
                    <div className="signup-split-recovery-engine-row">
                      <a
                        href={siteResult.engineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="signup-split-recovery-engine-link"
                      >
                        {siteResult.engineUrl}
                      </a>
                      <a
                        href={siteResult.engineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="signup-split-recovery-go-btn"
                      >
                        Go to my login page
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="signup-split-recovery-pending">
                    Your engine is still being set up. You&apos;ll receive an
                    email when it&apos;s ready.
                  </p>
                )}
              </section>
            ) : null}
            <button
              type="submit"
              className="signup-split-submit"
              disabled={loading}
            >
              {loading ? 'Working…' : 'Search'}
            </button>
            <div className="signup-split-login-prompt">
              <Link to="/signup" className="signup-split-secondary-link">
                Start a new free trial
              </Link>
            </div>
          </form>
        </div>

        <footer className="signup-split-footer">
          <div className="signup-split-legal">
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <span className="signup-split-legal-sep" aria-hidden>
              ·
            </span>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms
            </a>
          </div>
        </footer>
      </div>

      <div className="signup-split-right">
        <div className="signup-split-hero-wrap">
          <video
            className="signup-split-hero"
            src={bannerReorderDemo}
            autoPlay
            muted
            loop
            playsInline
            disablePictureInPicture
          />
        </div>
      </div>
    </div>
  );
}

export default AccountRecovery;
